import { useMemo } from "react";

import { useQueries } from "@tanstack/react-query";

import { entitiesApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import {
  CasePropertySchema,
  CaseQueueColumnSchemas,
  DeserializedCaseTemplateWithVersions,
  EnrichedDeserializedQueueCase,
} from "src/cases/queues/types";
import { LoadEntitiesRequestEnvEnum } from "src/clients/entities/api";
import { transformToFrontendEntity } from "src/entities/entityView/utils";
import { EntityResource, useEntitySchemas } from "src/entities/queries";
import { Environment } from "src/hooks/useEnvironment";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type UseEntityDisplayMapParams = {
  cases: EnrichedDeserializedQueueCase[];
  columnSchemas: CaseQueueColumnSchemas;
  caseTemplates: DeserializedCaseTemplateWithVersions[];
  environment: Environment;
  enabled?: boolean;
};

type EntityBatch = {
  schemaId: string;
  entityIds: string[];
};

type QueryResult = {
  schemaId: string;
  entities: EntityResource[];
};

const QUERY_STALE_TIME = 5 * 60 * 1000;
const BATCH_SIZE = 100;

const getEntityDisplayValueFromEntity = (
  entity: EntityResource,
  primaryProperty?: string,
): string => {
  const value = primaryProperty ? entity.properties?.[primaryProperty] : null;
  return typeof value === "string" && value ? value : entity._id;
};

const collectEntityReferences = (
  cases: EnrichedDeserializedQueueCase[],
  columnSchemas: CaseQueueColumnSchemas,
  caseTemplates: DeserializedCaseTemplateWithVersions[],
): Map<string, Set<string>> => {
  const refs = new Map<string, Set<string>>();

  cases.forEach((caseData) => {
    const template = caseTemplates.find((t) => t.id === caseData.template.id);
    const version = template?.versions.find(
      (v) => v.id === caseData.template_version.id,
    );
    if (!version) return;

    Object.values(columnSchemas).forEach((columnSchema) => {
      if (columnSchema?.type !== "entity_ref") return;

      const propertyKey = columnSchema.__key;
      const entityId = caseData.case_properties[propertyKey];
      const propertySchema = version.case_properties?.[propertyKey] as
        | CasePropertySchema
        | undefined;

      if (
        entityId &&
        typeof entityId === "string" &&
        propertySchema?.type === "entity_ref" &&
        "entity_type" in propertySchema
      ) {
        const schemaId = propertySchema.entity_type;
        if (!refs.has(schemaId)) {
          refs.set(schemaId, new Set());
        }
        refs.get(schemaId)!.add(entityId);
      }
    });
  });

  return refs;
};

const createEntityBatches = (
  entityReferences: Map<string, Set<string>>,
): EntityBatch[] => {
  const batches: EntityBatch[] = [];

  entityReferences.forEach((entityIds, schemaId) => {
    const idsArray = Array.from(entityIds);
    for (let i = 0; i < idsArray.length; i += BATCH_SIZE) {
      batches.push({
        schemaId,
        entityIds: idsArray.slice(i, i + BATCH_SIZE),
      });
    }
  });

  return batches;
};

export const useEntityDisplayMap = ({
  cases,
  columnSchemas,
  caseTemplates,
  environment,
  enabled = true,
}: UseEntityDisplayMapParams) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const { data: workspace } = useCurrentWorkspace();
  const { entitiesEnabled } = useWorkspaceFeatureGates();

  const apiClient = useMemo(
    () => (workspace?.base_url ? entitiesApi(workspace.base_url) : undefined),
    [workspace?.base_url],
  );

  const shouldFetchEntities = enabled && entitiesEnabled;

  const { data: schemasData, isLoading: schemasLoading } = useEntitySchemas({
    baseUrl: workspace?.base_url || "",
    options: { enabled: shouldFetchEntities && !!workspace?.base_url },
  });

  const schemaPrimaryProperties = useMemo(
    () =>
      new Map(
        schemasData?.entities
          ?.filter((schema) => schema._id && schema._primary_property)
          .map((schema) => [schema._id, schema._primary_property!]) || [],
      ),
    [schemasData],
  );

  const entityReferences = useMemo(
    () =>
      shouldFetchEntities
        ? collectEntityReferences(cases, columnSchemas, caseTemplates)
        : new Map(),
    [cases, columnSchemas, caseTemplates, shouldFetchEntities],
  );

  const entityBatches = useMemo(
    () => createEntityBatches(entityReferences),
    [entityReferences],
  );

  const entityQueries = useQueries({
    queries: entityBatches.map(({ schemaId, entityIds }) => ({
      queryKey: [
        "entities",
        "loadBatch",
        wsId,
        schemaId,
        environment,
        entityIds.sort(),
      ],
      queryFn: async (): Promise<QueryResult> => {
        if (!apiClient) {
          throw new Error("API client not available");
        }

        const response =
          await apiClient.loadEntitiesEntitiesApiV1OperationsLoadEntitiesPost({
            loadEntitiesRequest: {
              env: environment as LoadEntitiesRequestEnvEnum,
              entity_schema: schemaId,
              entity_ids: entityIds,
            },
          });

        const entitiesDict = response.data.data?.entities || {};
        const entities = Object.entries(entitiesDict).map(([id, entity]) =>
          transformToFrontendEntity({ ...entity, _id: id }),
        );

        return { schemaId, entities };
      },
      enabled: shouldFetchEntities && !!apiClient && entityIds.length > 0,
      staleTime: QUERY_STALE_TIME,
    })),
  });

  const entityQueryData = entityQueries.map((q) => q.data);

  const displayMap = useMemo(() => {
    const map: Record<string, string> = {};

    // Only process if entities are enabled
    if (!entitiesEnabled) {
      return map;
    }

    entityQueryData.forEach((data) => {
      if (!data) return;

      const { schemaId, entities } = data;
      const primaryProperty = schemaPrimaryProperties.get(schemaId);

      entities.forEach((entity) => {
        map[entity._id] = getEntityDisplayValueFromEntity(
          entity,
          primaryProperty,
        );
      });
    });

    return map;
  }, [entityQueryData, schemaPrimaryProperties, entitiesEnabled]);

  const isLoading =
    shouldFetchEntities &&
    (schemasLoading || entityQueries.some((query) => query.isLoading));

  return { displayMap, isLoading };
};
